import React, { useState, useEffect } from 'react';
import { Cookie, X, Check, Settings } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Get initial preferences from localStorage
const getInitialPreferences = () => {
  try {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      return JSON.parse(consent);
    }
  } catch {
    // Invalid consent data
  }
  return {
    essential: true,
    analytics: false,
    functional: false,
    marketing: false
  };
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(getInitialPreferences);
  const { t } = useLanguage();

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Delay showing the banner for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      functional: true,
      marketing: true
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setPreferences(allAccepted);
    setIsVisible(false);
    
    // Initialize analytics if accepted
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted'
      });
    }
  };

  const handleAcceptSelected = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setIsVisible(false);
    
    // Update analytics consent based on preferences
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied',
        'ad_storage': preferences.marketing ? 'granted' : 'denied'
      });
    }
  };

  const handleDeclineOptional = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      functional: false,
      marketing: false
    };
    localStorage.setItem('cookieConsent', JSON.stringify(essentialOnly));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setPreferences(essentialOnly);
    setIsVisible(false);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Can't disable essential cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 z-[99999] flex items-end justify-center p-4 pointer-events-none"
      data-testid="cookie-consent-overlay"
    >
      <div 
        className="w-full max-w-2xl bg-[#0b004c] border border-white/20 rounded-xl shadow-2xl pointer-events-auto animate-slideUp"
        data-testid="cookie-consent-banner"
      >
        {/* Main Banner */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Cookie className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-montserrat-bold text-lg mb-2">
                {t('cookieConsentTitle')}
              </h3>
              <p className="text-white/80 text-sm mb-4">
                {t('cookieConsentDesc')}
              </p>
              
              {/* Settings Panel */}
              {showSettings && (
                <div className="mb-4 p-4 bg-white/10 rounded-lg space-y-3" data-testid="cookie-settings-panel">
                  {/* Essential Cookies */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium text-sm">{t('cookieEssential')}</p>
                      <p className="text-white/60 text-xs">{t('cookieEssentialDesc')}</p>
                    </div>
                    <div className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs">
                      {t('cookieAlwaysOn')}
                    </div>
                  </div>
                  
                  {/* Analytics Cookies */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium text-sm">{t('cookieAnalytics')}</p>
                      <p className="text-white/60 text-xs">{t('cookieAnalyticsDesc')}</p>
                    </div>
                    <button
                      onClick={() => togglePreference('analytics')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.analytics ? 'bg-green-500' : 'bg-white/20'
                      }`}
                      data-testid="cookie-toggle-analytics"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                        preferences.analytics ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  
                  {/* Functional Cookies */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium text-sm">{t('cookieFunctional')}</p>
                      <p className="text-white/60 text-xs">{t('cookieFunctionalDesc')}</p>
                    </div>
                    <button
                      onClick={() => togglePreference('functional')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.functional ? 'bg-green-500' : 'bg-white/20'
                      }`}
                      data-testid="cookie-toggle-functional"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                        preferences.functional ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  
                  {/* Marketing Cookies */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium text-sm">{t('cookieMarketing')}</p>
                      <p className="text-white/60 text-xs">{t('cookieMarketingDesc')}</p>
                    </div>
                    <button
                      onClick={() => togglePreference('marketing')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.marketing ? 'bg-green-500' : 'bg-white/20'
                      }`}
                      data-testid="cookie-toggle-marketing"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                        preferences.marketing ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleAcceptAll}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-[#0b004c] rounded-lg font-medium text-sm hover:bg-white/90 transition-colors"
                  data-testid="cookie-accept-all-btn"
                >
                  <Check className="w-4 h-4" />
                  {t('cookieAcceptAll')}
                </button>
                
                {showSettings ? (
                  <button
                    onClick={handleAcceptSelected}
                    className="flex items-center gap-2 px-4 py-2 border border-white/30 text-white rounded-lg font-medium text-sm hover:bg-white/10 transition-colors"
                    data-testid="cookie-accept-selected-btn"
                  >
                    {t('cookieSavePreferences')}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowSettings(true)}
                    className="flex items-center gap-2 px-4 py-2 border border-white/30 text-white rounded-lg font-medium text-sm hover:bg-white/10 transition-colors"
                    data-testid="cookie-settings-btn"
                  >
                    <Settings className="w-4 h-4" />
                    {t('cookieSettings')}
                  </button>
                )}
                
                <button
                  onClick={handleDeclineOptional}
                  className="px-4 py-2 text-white/70 text-sm hover:text-white transition-colors"
                  data-testid="cookie-decline-btn"
                >
                  {t('cookieDecline')}
                </button>
              </div>
            </div>
            
            {/* Close button */}
            <button
              onClick={handleDeclineOptional}
              className="flex-shrink-0 text-white/50 hover:text-white transition-colors"
              aria-label="Close"
              data-testid="cookie-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
